import { useTranslation } from 'react-i18next'
import { Switch } from '@/components/ui/switch'
import type { FaceMatchingReference } from '@/services/api/face-matching'
import { FaceTile } from './FaceTile'

export interface FaceReferenceStripProps {
  references: FaceMatchingReference[]
  /**
   * Flip a confirmed face into or out of the reference set (ADR-0039). Omitted
   * for a viewer (PRD-0032 SC-036): the switches then only show the set.
   */
  onToggleReference?: (faceId: string, reference: boolean) => void
  /** The face whose toggle is in flight; its switch stays put until it answers. */
  togglingFaceId?: string | null
}

/**
 * The identity's confirmed faces, each with the switch that decides whether it
 * feeds the matching. Pure — the caller owns the request behind the switch, and
 * says why when it offers none.
 */
export function FaceReferenceStrip({
  references,
  onToggleReference,
  togglingFaceId = null,
}: FaceReferenceStripProps) {
  const { t } = useTranslation()
  const base = 'cases.investigate.entityResolution.photoMatching'
  return (
    <div className="flex flex-col gap-2">
      <span className="text-2xs font-medium uppercase tracking-wider text-muted-foreground">
        {t(`${base}.references`, { count: references.length })}
      </span>
      {references.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t(`${base}.noReferences`)}</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {references.map(reference => (
            <div key={reference.face_id} className="flex flex-col items-center gap-1">
              <FaceTile
                url={reference.crop_url}
                alt={reference.document_name ?? t(`${base}.columns.source`)}
                size="lg"
              />
              <label className="flex items-center gap-1 text-2xs text-muted-foreground">
                <Switch
                  size="sm"
                  checked={reference.reference}
                  disabled={!onToggleReference || togglingFaceId === reference.face_id}
                  aria-label={t(`${base}.actions.useAsReference`)}
                  data-face-id={reference.face_id}
                  onCheckedChange={next => onToggleReference?.(reference.face_id, next)}
                />
                {t(`${base}.actions.useAsReference`)}
              </label>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
